import { mergeClasses } from '@fluentui/react-components';
import { KnownInstrumentationEvents } from '@office-start/instrumentation-types';
import { useLocStrings } from '@office-start/loc-service';
import { useIsDarkMode, useThemeGenerator, } from '@office-start/theming-service';
import { useCallback, useMemo } from 'react';
import { getThemedImageFromCallout } from '../getThemedImageFromCallout';
import { useTeachingUiStyles } from '../TeachingUiContainerComponent.styles';
import { logTuiAction, logTuiFeature, } from '../tuiInstrumentation';
export function useTeachingBubbleProps(_a) {
    var currentStep = _a.currentStep, displayIndex = _a.displayIndex, totalSteps = _a.totalSteps, onComplete = _a.onComplete, customActionProps = _a.customActionProps, componentContext = _a.componentContext, next = _a.next, previous = _a.previous, link = _a.link;
    var locStrings = useLocStrings().data;
    // Parse applicable content
    var teachingComponentStrings = (locStrings !== null && locStrings !== void 0 ? locStrings : {}).teachingUIComponentResources;
    var darkModeEnabled = useIsDarkMode();
    var fluentClassName = useThemeGenerator();
    var styles = useTeachingUiStyles();
    var onLinkClick = useCallback(function () {
        if (link && currentStep) {
            var eventName = KnownInstrumentationEvents.TuiLinkClick;
            var actionId = '"freemiumreminder_learnmore';
            logTuiAction(actionId, eventName, 'Navigate');
            logTuiFeature(KnownInstrumentationEvents.TuiLinkClick, {
                currentStep: currentStep,
            });
        }
    }, [currentStep, link]);
    var navigateCalloutIndex = useCallback(function (direction) {
        if (currentStep) {
            var eventName = KnownInstrumentationEvents.TuiActionButtonClick;
            var subArea = direction === 'next'
                ? 'TuiNextButton'
                : 'TuiPreviousButton';
            logTuiAction(direction, eventName, 'Navigate', subArea);
            logTuiFeature(eventName, { currentStep: currentStep, subArea: subArea });
            // Update state to show next or previous callout
            direction === 'next' ? next() : previous();
        }
    }, [next, previous, currentStep]);
    var doneButtonProps = useMemo(function () {
        if (!teachingComponentStrings || !currentStep) {
            return undefined;
        }
        var actionButton = currentStep.actionButton;
        if (actionButton) {
            return {
                tabIndex: 0,
                children: teachingComponentStrings[actionButton.text],
                onClick: function () {
                    onComplete('completed');
                    actionButton.action(customActionProps);
                },
            };
        }
        var onClick = function () {
            onComplete('completed');
        };
        var doneProps = {
            tabIndex: 0,
            children: totalSteps === 1
                ? teachingComponentStrings.upgradeToPremiumClosingButton
                : teachingComponentStrings.calloutDoneButtonText,
            onClick: onClick,
        };
        return doneProps;
    }, [
        teachingComponentStrings,
        currentStep,
        totalSteps,
        onComplete,
        customActionProps,
    ]);
    var nextButtonProps = useMemo(function () {
        if (!teachingComponentStrings) {
            return undefined;
        }
        var nextProps = {
            tabIndex: 0,
            children: teachingComponentStrings.calloutNextButtonText,
            onClick: function () {
                navigateCalloutIndex('next');
            },
        };
        return nextProps;
    }, [teachingComponentStrings, navigateCalloutIndex]);
    // Setup buttons
    var primaryButtonProps = useMemo(function () {
        if (!currentStep || !teachingComponentStrings) {
            return undefined;
        }
        if (displayIndex === totalSteps) {
            return doneButtonProps;
        }
        return nextButtonProps;
    }, [
        currentStep,
        displayIndex,
        totalSteps,
        teachingComponentStrings,
        nextButtonProps,
        doneButtonProps,
    ]);
    var secondaryButtonProps = useMemo(function () {
        if (!currentStep || displayIndex === 1 || !teachingComponentStrings) {
            return undefined;
        }
        var props = {
            tabIndex: 0,
            children: teachingComponentStrings.calloutPreviousButtonText,
            onClick: function () {
                navigateCalloutIndex('previous');
            },
        };
        return props;
    }, [
        currentStep,
        displayIndex,
        teachingComponentStrings,
        navigateCalloutIndex,
    ]);
    var imageProps = useMemo(function () {
        var themedImage = getThemedImageFromCallout(currentStep, componentContext);
        if (!themedImage) {
            return undefined;
        }
        var themedImageProps = {
            src: darkModeEnabled ? themedImage.dark : themedImage.light,
            className: styles.imageContainer,
            shouldFadeIn: false,
            role: 'presentation',
        };
        return themedImageProps;
    }, [currentStep, darkModeEnabled, componentContext, styles]);
    var teachingBubbleClassname = useMemo(function () {
        return mergeClasses(fluentClassName, styles.calloutRoot, darkModeEnabled ? styles.calloutRootDark : undefined);
    }, [fluentClassName, darkModeEnabled, styles]);
    return useMemo(function () { return ({
        primaryButtonProps: primaryButtonProps,
        secondaryButtonProps: secondaryButtonProps,
        imageProps: imageProps,
        teachingBubbleClassname: teachingBubbleClassname,
        onLinkClick: onLinkClick,
        closeButtonAriaLabel: locStrings === null || locStrings === void 0 ? void 0 : locStrings.teachingUIComponentResources.calloutCloseButtonAriaLabel,
    }); }, [
        primaryButtonProps,
        secondaryButtonProps,
        imageProps,
        teachingBubbleClassname,
        onLinkClick,
        locStrings,
    ]);
}
//# sourceMappingURL=useTeachingBubbleProps.js.map